import { TextField } from '@material-ui/core';
import type { StandardTextFieldProps, TextFieldProps as MuiTextFieldProps } from '@material-ui/core';
import { Language, Power } from '@material-ui/icons';
import { Button, Alert, CircularProgress, Grow, Typography } from '@mui/material';
import { getIn } from 'formik';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { Hint } from 'v2/components/Shared/Hint';
import useMask from 'v2/hooks/useMask';

import webhookService from '../service';
import styles from './styles';

import type { TestResult } from '../interfaces';
import type { FieldProps } from 'formik';

export type TextFieldProps = FieldProps &
  Omit<StandardTextFieldProps, 'ref' | 'variant'> & {
    mask?: string;
    variant?: MuiTextFieldProps['variant'];
    onValidateUrl: (valid: boolean) => void;
  };

export const UrlValidationFormField = ({ field, form, helperText, ...props }: TextFieldProps) => {
  const classes = styles();
  const { name } = field;
  const { mask, onValidateUrl } = props;

  const [result, setResult] = React.useState<TestResult>();
  const [validating, setValidating] = React.useState(false);
  const value = field?.value || '';
  const { maskedValue, maskClean } = useMask(mask, value);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setResult(null);
      onValidateUrl(false);
      return form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [onValidateUrl, form, field.name, maskClean]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;

      form.setFieldTouched(field.name, true);

      if (!v) return;

      form.setFieldValue(field.name, maskClean(e.target.value));
    },
    [form, field.name, maskClean]
  );

  const validUrl = useCallback(async () => {
    try {
      setValidating(true);
      const values = form.values;
      const result = await webhookService.validateUrl(values.url, values.type);
      setValidating(false);
      setResult(result.data);
      onValidateUrl(result?.data?.valid);
    } catch (e) {
      toast.error('Ocorreu um erro ao validar a url');
      setValidating(false);
    }
  }, [onValidateUrl, form]);

  const errorText = !!(form.touched[field.name] && form.errors[field.name]) && getIn(form.errors, field.name);
  const showResult = !!result?.requestData;
  const urlSuccess = showResult && result?.valid;
  const urlFailed = showResult && !result?.valid;
  const isUrlFormatValid = !errorText;

  return (
    <>
      <Hint inputName={field.name}>
        <div className={classes.contentInput}>
          <TextField
            fullWidth
            error={!!errorText}
            name={name}
            value={maskedValue}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            variant='standard'
            {...props}
            helperText={helperText}
            label='URL'
          />
          <Button
            variant='contained'
            onClick={validUrl}
            style={{ margin: '0 0 0 12px' }}
            startIcon={validating ? <CircularProgress size={12} style={{ color: '#596375' }} /> : <Power />}
            disabled={!isUrlFormatValid}
          >
            Verificar
          </Button>
        </div>
      </Hint>
      {showResult && (
        <div className={classes.paddingMsg}>
          {urlSuccess && (
            <Alert severity='success' className={classes.alert}>
              Url verificada com sucesso!
            </Alert>
          )}
          {urlFailed && (
            <Alert
              severity='error'
              className={classes.alert}
              action={
                <>
                  <a href='https://eduzz.github.io/webhook' target='_blank' rel='noreferrer'>
                    <Button> Ajuda</Button>
                  </a>
                </>
              }
            >
              <p>Tentativa de envio teste para url falhou!</p>
              <p>Verifique a resposta da url abaixo</p>
            </Alert>
          )}
          <Grow in={showResult}>
            <div className={classes.paddingMsg}>
              <Typography component='p'>Dados enviados</Typography>
              <div className={classes.pre}>
                <p>
                  <b>POST</b> {value}
                </p>
              </div>
            </div>
          </Grow>

          <Grow in>
            <div className={classes.paddingMsg}>
              <Typography component='p'>Resposta da url</Typography>
              <div className={classes.pre}>
                <a target='_blank' href={`https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Status/${result?.response.status}`} rel='noreferrer'>
                  <div className={classes.item}>
                    <Language />
                    <Typography className={classes.status}>
                      Status: {result?.response.status} {result?.response.statusText}
                    </Typography>
                  </div>
                </a>
                <p> {result?.response.helperText}</p>
                <p style={{ fontSize: 14, marginTop: 12, fontWeight: 600 }}>Tempo de resposta: {`${result?.response.duration || 0} ms`}</p>
              </div>
            </div>
          </Grow>
        </div>
      )}
    </>
  );
};

export default UrlValidationFormField;
